import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PlusIcon } from '@heroicons/react/24/solid'
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type Cell,
    type ColumnDef,
    type ColumnOrderState,
    type Row,
    type SortingState,
    type VisibilityState,
    type PaginationState,
} from '@tanstack/react-table'
import { AdjustmentsHorizontalIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/solid'
import { JSTDate, JSTTime, getAge } from 'contexts/dateUtils'
import options from 'contexts/options.json'

/**
 * Retrieves a nested value from an object using a dot-notation path.
 * 
 * @param obj - The object to traverse
 * @param path - Dot-notation path to the nested property (e.g., "user.profile.name")
 * @returns The value at the specified path, or undefined if the path doesn't exist
 * 
 * @example
 * const obj = { user: { profile: { name: "John" } } }
 * getNestedValue(obj, "user.profile.name") // Returns "John"
 */
export const getNestedValue = (obj: KV, path: string): any => {
    return path.split('.').reduce((current: any, key: string) => current?.[key], obj)
}

/**
 * Cell generators for rendering different data types in table cells.
 * Each generator returns a function that takes a row and returns a formatted string.
 */
export const cellGenerators = {
    /**
     * Formats a date value as JST date string.
     * 
     * @param field - The field name to access in the row object (supports dot-notation for nested fields)
     * @returns A function that formats the date value from a row
     */
    Date: (field: string) => (row: KV) => {
        const value = getNestedValue(row, field)
        return value ? JSTDate(new Date(value)) : ""
    },
    /**
     * Formats a time value as JST time string.
     * Returns empty string if the modality is in noslotResources.
     * 
     * @param field - The field name to access in the row object (supports dot-notation for nested fields)
     * @returns A function that formats the time value from a row
     */
    Time: (field: string) => (row: KV, noslotResources: string[] = []) => {
        const value = getNestedValue(row, field)
        if (!value) return ""
        if (noslotResources.includes(row.modalityID)) return ""
        return JSTTime(new Date(value))
    },
    /**
     * Renders a time string (HH:mm) directly without checking noslot resources.
     */
    TimeString: (field: string) => (row: KV) => {
        const value = getNestedValue(row, field)
        return value || ""
    },
    /**
     * Calculates and formats age from a date value.
     * 
     * @param field - The field name to access in the row object (supports dot-notation for nested fields)
     * @returns A function that calculates age from a date in a row
     */
    Age: (field: string) => (row: KV) => {
        const value = getNestedValue(row, field)
        return value ? getAge(new Date(value)) : ""
    },
    /**
     * Formats a booking status value using the options mapping.
     * 
     * @param field - The field name containing the status value
     * @returns A function that formats the status from a row
     */
    Status: (field: string) => (row: KV) => options.bookingStatus[(row as any)[field]],
    /**
     * Formats an option value using the options mapping.
     * 
     * @param field - The field name to access in the row object (supports dot-notation for nested fields)
     * @param optionKey - The key in the options object to use for mapping
     * @returns A function that formats the option value from a row
     */
    Option: (field: string, optionKey?: keyof typeof options) => (row: KV) => {
        const value = getNestedValue(row, field)
        return value !== undefined && optionKey && options[optionKey] ? options[optionKey][value] : ""
    },
    /**
     * Formats a string value, handling nested fields.
     * 
     * @param field - The field name to access in the row object (supports dot-notation for nested fields)
     * @returns A function that formats the string value from a row
     */
    String: (field: string) => (row: KV) => {
        const value = getNestedValue(row, field)
        return value?.toString() || ""
    },
    /**
     * Formats contrast value as "◯" if contrast is true or "造影", otherwise empty string.
     * 
     * @returns A function that formats the contrast value from a row
     */
    Contrast: () => (row: KV) => (row.contrast === true || row.contrast === "造影") ? "◯" : "",
    /**
     * Formats an array value by joining elements with " / ".
     * 
     * @param field - The field name to access in the row object (supports dot-notation for nested fields)
     * @returns A function that formats the array value from a row
     */
    Array: (field: string) => (row: KV) => {
        const value = getNestedValue(row, field)
        return Array.isArray(value) && value.length ? value.join(' / ') : ""
    },
}

/**
 * Base type for table column header definitions ("heads").
 *
 * This captures the common properties shared by header configs in pages like
 * booking/list and reporting/list so they can share a single source of truth.
 *
 * - `field` is the unique column identifier used by the table.
 * - `id` is the optional underlying database field name used for sorting / querying.
 *   When omitted, it is treated as the same as `field`.
 */
export type TableHeadBase = {
    /** Unique column identifier used by the table */
    field: string
    /** Display text for the column header */
    text: string
    /** Optional database field name (for sorting / querying). Defaults to `field` when omitted. */
    id?: string
    /** Logical cell type. Defaults to "String" when omitted. */
    type?: keyof typeof cellGenerators
    /** Optional key into `options` for Option-type cells (e.g. "genderType", "bookingType") */
    optionKey?: keyof typeof options
    /** Optional accessor function used by TanStack Table */
    accessorFn?: (row: KV) => any
    /** Optional custom cell renderer. Second argument is used only where needed (e.g. noslotResources in booking list). */
    cell?: (row: KV, noslotResources?: string[]) => React.ReactNode
}

/**
 * Helper to create TanStack Table columns from an array of `TableHeadBase` configs.
 *
 * This centralizes the default behavior for:
 * - Mapping logical `type` to `cellGenerators`
 * - Using `optionKey` for Option cells (with raw-value fallback when no mapping is found)
 * - Falling back to simple string rendering when no specific generator exists
 *
 * Page components (e.g. reporting list) can use this to avoid duplicating column creation logic,
 * and still customize specific columns afterwards if needed.
 */
export type CreateColumnsOptions = {
    /** For Time-type cells, list of modality IDs that should hide time display */
    noslotResources?: string[]
}

// Helper to check and compare time strings (H:mm or HH:mm)
const timeRegex = /^(\d{1,2}):(\d{2})$/

const compareTimeStrings = (a: string, b: string): number | null => {
    const aMatch = a.match(timeRegex)
    const bMatch = b.match(timeRegex)

    if (aMatch && bMatch) {
        const aH = parseInt(aMatch[1], 10)
        const aM = parseInt(aMatch[2], 10)
        const bH = parseInt(bMatch[1], 10)
        const bM = parseInt(bMatch[2], 10)

        if (aH !== bH) return aH - bH
        return aM - bM
    }
    return null
}

// Custom sorting function for time strings
const timeSort = (rowA: Row<KV>, rowB: Row<KV>, columnId: string) => {
    const rawA = rowA.getValue(columnId)
    const rawB = rowB.getValue(columnId)

    let a = (rawA === null || rawA === undefined) ? '' : rawA
    let b = (rawB === null || rawB === undefined) ? '' : rawB

    if (typeof a === 'string') {
        a = a.trim()
    }
    if (typeof b === 'string') {
        b = b.trim()
    }

    const aEmpty = a === ''
    const bEmpty = b === ''

    if (aEmpty && bEmpty) return 0
    if (aEmpty) return -1
    if (bEmpty) return 1

    // Try to sort as time strings if applicable
    if (typeof a === 'string' && typeof b === 'string') {
        const timeComparison = compareTimeStrings(a, b)
        if (timeComparison !== null) return timeComparison
    }

    if ((a as any) < (b as any)) return -1
    if ((a as any) > (b as any)) return 1
    return 0
}

// Custom sorting function to handle trimmed strings and empty values consistently
const trimmedSort = (rowA: Row<KV>, rowB: Row<KV>, columnId: string) => {
    const rawA = rowA.getValue(columnId)
    const rawB = rowB.getValue(columnId)

    let a = (rawA === null || rawA === undefined) ? '' : rawA
    let b = (rawB === null || rawB === undefined) ? '' : rawB

    if (typeof a === 'string') {
        a = a.trim()
    }
    if (typeof b === 'string') {
        b = b.trim()
    }

    const aEmpty = a === ''
    const bEmpty = b === ''

    if (aEmpty && bEmpty) return 0
    if (aEmpty) return -1
    if (bEmpty) return 1

    if (typeof a === 'number' && typeof b === 'number') {
        return a - b
    }

    if ((a as any) < (b as any)) return -1
    if ((a as any) > (b as any)) return 1
    return 0
}

export const createColumnsFromHeads = (heads: TableHeadBase[], optionsArg?: CreateColumnsOptions): ColumnDef<KV>[] => {
    return heads.map(head => {
        const headWithId = head as typeof head & { id?: string }
        const dbField = headWithId.id || head.field

        const columnDef: any = {
            id: head.field,
            header: head.text,
            enableSorting: true,
            // Use accessorFn to safely retrieve and normalize values for consistent sorting
            accessorFn: (row: KV) => {
                const val = getNestedValue(row, dbField)
                if (val === null || val === undefined || val === '') return ""
                if (typeof val === 'string' && val.trim() === '') return ""
                return val
            },
            sortingFn: trimmedSort,
        }

        if ('accessorFn' in head && head.accessorFn) {
            columnDef.accessorFn = head.accessorFn
        }

        const cellType = head.type || 'String'

        // If a custom cell renderer is provided on the head, prefer it over built-in generators.
        if ('cell' in head && typeof head.cell === 'function') {
            const customCell = head.cell as (row: KV, extras?: any) => React.ReactNode
            columnDef.cell = ({ row }: { row: { original: KV } }) => customCell(row.original)
        } else if (cellType in cellGenerators) {
            if (cellType === 'Option') {
                const optionKey: keyof typeof options | undefined = head.optionKey
                const optionCell = cellGenerators.Option(dbField, optionKey)
                columnDef.cell = ({ row }: { row: { original: KV } }) => {
                    const mapped = optionCell(row.original)
                    if (mapped !== '') return mapped
                    const raw = getNestedValue(row.original, dbField)
                    return raw !== undefined && raw !== null ? raw.toString() : ''
                }
            } else if (cellType === 'Contrast') {
                const contrastCell = cellGenerators.Contrast()
                columnDef.cell = ({ row }: { row: { original: KV } }) => contrastCell(row.original)
            } else if (cellType === 'Status') {
                const statusCell = cellGenerators.Status(dbField)
                columnDef.cell = ({ row }: { row: { original: KV } }) => statusCell(row.original)
            } else if (cellType === 'Time') {
                const timeCell = cellGenerators.Time(dbField)
                const noslotResources = optionsArg?.noslotResources ?? []
                columnDef.cell = ({ row }: { row: { original: KV } }) =>
                    timeCell(row.original, noslotResources)
                // Override accessor to handle noslot resources for sorting
                columnDef.accessorFn = (row: KV) => {
                    if (noslotResources.includes(row.modalityID)) return ""
                    const val = getNestedValue(row, dbField)
                    if (val === null || val === undefined || val === '') return ""
                    if (typeof val === 'string' && val.trim() === '') return ""
                    return val
                }
                columnDef.sortingFn = timeSort
            } else if (cellType === 'TimeString') {
                const timeStringCell = cellGenerators.TimeString(dbField)
                columnDef.cell = ({ row }: { row: { original: KV } }) =>
                    timeStringCell(row.original)
                columnDef.sortingFn = timeSort
            } else if (cellType === 'Date') {
                columnDef.cell = ({ row }: { row: { original: KV } }) =>
                    cellGenerators.Date(dbField)(row.original)
            } else if (cellType === 'Age') {
                columnDef.cell = ({ row }: { row: { original: KV } }) =>
                    cellGenerators.Age(dbField)(row.original)
            } else if (cellType === 'Array') {
                columnDef.cell = ({ row }: { row: { original: KV } }) =>
                    cellGenerators.Array(dbField)(row.original)
            } else if (cellType === 'String') {
                columnDef.cell = ({ row }: { row: { original: KV } }) => {
                    const value = getNestedValue(row.original, dbField)
                    return value?.toString() || ''
                }
            } else {
                columnDef.cell = ({ row }: { row: { original: KV } }) => {
                    const value = getNestedValue(row.original, dbField)
                    return value?.toString() || ''
                }
            }
        }

        return columnDef as ColumnDef<KV>
    })
}

/**
 * Joins an array of strings with " / " separator.
 * 
 * @param value - Optional array of strings to join
 * @returns Joined string or empty string if value is undefined or empty
 * 
 * @example
 * joinValues(["A", "B", "C"]) // Returns "A / B / C"
 * joinValues([]) // Returns ""
 * joinValues(undefined) // Returns ""
 */
export const joinValues = (value?: string[]) => (value && value.length ? value.join(' / ') : "")

/**
 * Props for the DataTable component.
 * 
 * @template TData - The type of data in each row
 */
export type DataTableProps<TData> = {
    /** Array of data objects to display in the table */
    data: TData[]
    /** Column definitions for the table using TanStack Table's ColumnDef */
    columns: ColumnDef<TData, any>[]
    /** Optional content to display in the filter area (above the table) */
    filterContent?: React.ReactNode
    /** Optional callback function to show an "Add" button (floating action button) */
    onAdd?: () => void
    /** Total count of items (used for display, especially with server-side pagination) */
    itemCount?: number
    /** CSS class name for the container div */
    containerClassName?: string
    /** CSS class name for the table element */
    tableClassName?: string
    /** Optional function to get custom props for table rows */
    getRowProps?: (row: Row<TData>) => React.HTMLAttributes<HTMLTableRowElement> & { key?: React.Key }
    /** Optional function to get custom props for table cells */
    getCellProps?: (cell: Cell<TData, unknown>) => React.TdHTMLAttributes<HTMLTableCellElement> & { key?: React.Key }
    /** Whether to enable pagination controls (default: true) */
    enablePagination?: boolean
    /** Number of items per page (default: 50) */
    pageSize?: number
    /** Custom pagination content to replace the default pagination controls */
    paginationContent?: React.ReactNode
    /** Callback function called when pagination state changes (for server-side pagination) */
    onPaginationChange?: (pagination: PaginationState) => void
    /** 
     * Total number of pages. If not provided and itemCount is available, will be calculated automatically.
     * Only needed when using manual pagination (server-side pagination).
     */
    pageCount?: number
    /** 
     * Callback function called when sorting state changes.
     * Only called when manualPagination is true (for server-side sorting).
     * When manualPagination is false, sorting is handled client-side and this callback is not used.
     * 
     * @param sorting - The new sorting state from TanStack Table
     */
    onSortingChange?: (sorting: SortingState) => void
    /** 
     * Whether to show a row number column as the first column.
     * When enabled, adds a "No" column that displays sequential row numbers.
     * Row numbers automatically reflect pagination:
     * - Page 1 (100 items/page): rows 1-100
     * - Page 2 (100 items/page): rows 101-200
     * - Page 3 (100 items/page): rows 201-300
     * Without pagination, shows sequential numbers starting from 1.
     */
    showRowNumber?: boolean
    /**
     * Optional external pagination state for full control from parent.
     * When provided together with onPaginationChange, DataTable will treat pagination as controlled:
     * - It will not update its own internal pagination state.
     * - It will always use this external state for page index/size and row numbers.
     */
    externalPagination?: PaginationState
    /**
     * Optional external sorting state for full control from parent.
     * When provided together with onSortingChange, DataTable will treat sorting as controlled:
     * - It will not update its own internal sorting state.
     * - It will always use this external state for header styles and sort indicators.
     */
    externalSorting?: SortingState
    initialColumnOrder?: ColumnOrderState
    initialColumnVisibility?: VisibilityState
    onColumnOrderChange?: (updater: ColumnOrderState | ((old: ColumnOrderState) => ColumnOrderState)) => void
    onColumnVisibilityChange?: (updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => void
    /** 
     * Optional key for persisting column settings (order and visibility) to localStorage.
     * If provided, settings will be automatically saved and restored.
     */
    storageKey?: string
}

/**
 * A flexible, feature-rich data table component built on TanStack Table.
 * 
 * Features:
 * - Sortable columns (click headers to sort)
 *   - Client-side sorting when all data is loaded (default)
 *   - Server-side sorting when using manualPagination (triggers refetch)
 * - Draggable column reordering
 * - Column visibility toggle
 * - Pagination (client-side or server-side)
 * - Optional row number column (automatically reflects pagination)
 * - Customizable row and cell rendering
 * - Optional floating "Add" button
 * - Automatic persistence of column settings (optional via storageKey)
 * 
 * @template TData - The type of data in each row
 * 
 * @param props - DataTableProps configuration object
 * @returns JSX element representing the data table
 */
export function DataTable<TData>({
    data,
    columns,
    filterContent,
    onAdd,
    itemCount,
    containerClassName = 'w-full h-full',
    tableClassName = 'mx-auto border-b border-gray-200 sm:rounded-b-md text-center text-sm text-gray-500',
    getRowProps,
    getCellProps,
    enablePagination = true,
    pageSize = 50,
    paginationContent,
    onPaginationChange,
    pageCount: pageCountProp,
    onSortingChange,
    showRowNumber = false,
    externalPagination,
    externalSorting,
    initialColumnOrder = [],
    initialColumnVisibility = {},
    onColumnOrderChange,
    onColumnVisibilityChange,
    storageKey,
}: DataTableProps<TData>) {
    // ============================================================================
    // State Management
    // ============================================================================
    const [sorting, setSorting] = useState<SortingState>([])
    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(initialColumnOrder)
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialColumnVisibility)
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: pageSize,
    })
    const [draggedColumn, setDraggedColumn] = useState<string | null>(null)
    const [showColumnMenu, setShowColumnMenu] = useState(false)

    // ============================================================================
    // Persistence
    // ============================================================================
    useEffect(() => {
        if (!storageKey) return
        try {
            const saved = localStorage.getItem(storageKey)
            if (saved) {
                const parsed = JSON.parse(saved)
                if (parsed.order) setColumnOrder(parsed.order)
                if (parsed.visibility) setColumnVisibility(parsed.visibility)
            }
        } catch (e) {
            console.error('Failed to load table column settings', e)
        }
    }, [storageKey])

    // ============================================================================
    // Refs for Stable Access
    // ============================================================================
    // Use refs to access current state in callbacks without causing dependency issues
    const effectivePagination = externalPagination ?? pagination
    // Only use externalSorting when doing manual/server-side pagination.
    // For single-page (client-side) cases, rely on internal sorting so headers update correctly.
    const shouldUseExternalSorting = Boolean(externalSorting && onSortingChange && itemCount !== undefined && itemCount > effectivePagination.pageSize)
    const effectiveSorting = shouldUseExternalSorting ? externalSorting! : sorting

    const paginationRef = useRef(effectivePagination)
    const sortingRef = useRef(effectiveSorting)
    paginationRef.current = effectivePagination
    sortingRef.current = effectiveSorting

    // ============================================================================
    // Computed Values
    // ============================================================================
    // Auto-detect if manual pagination is needed:
    // - If onPaginationChange is provided AND itemCount > pageSize, use manual pagination
    const isManualPagination = useMemo(() => {
        if (onPaginationChange && itemCount !== undefined && itemCount > effectivePagination.pageSize) return true
        return false
    }, [onPaginationChange, itemCount, effectivePagination.pageSize])

    // Calculate pageCount automatically if itemCount is provided and pageCount is not explicitly set
    const pageCount = useMemo(() => {
        if (pageCountProp !== undefined) return pageCountProp
        if (itemCount !== undefined && effectivePagination.pageSize > 0) {
            return Math.ceil(itemCount / effectivePagination.pageSize)
        }
        return undefined
    }, [pageCountProp, itemCount, effectivePagination.pageSize])

    // Store row number column definition in a ref to keep it stable and avoid dependency issues
    const rowNumberColumnRef = useRef<ColumnDef<TData, any>>({
        id: 'rowNumber',
        header: 'No',
        enableSorting: false,
        cell: ({ row, table: tableInstance }) => {
            // Calculate row number based on pagination
            // Access pagination state dynamically from table instance
            // Check if pagination is enabled by checking if pagination state exists
            const paginationState = tableInstance.getState().pagination
            if (paginationState) {
                return paginationState.pageIndex * paginationState.pageSize + row.index + 1
            }
            return row.index + 1
        },
    })

    // ============================================================================
    // Event Handlers
    // ============================================================================
    // Create wrapper functions that update internal state and call parent callbacks
    // These must be defined before useReactTable
    // Use refs to access current state without causing dependency issues
    const handlePaginationChange = useCallback(
        (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
            const newPagination =
                typeof updater === 'function' ? updater(paginationRef.current) : updater
            // Debug: track pagination changes at table level
            // eslint-disable-next-line no-console
            console.log('[DataTable] handlePaginationChange', {
                prev: paginationRef.current,
                next: newPagination,
                controlled: Boolean(externalPagination),
            })

            // If externalPagination is provided, treat pagination as controlled:
            // do not update internal state, just notify parent.
            if (!externalPagination) {
                setPagination(newPagination)
            }
            if (enablePagination && onPaginationChange) {
                onPaginationChange(newPagination)
            }
        },
        [enablePagination, onPaginationChange, externalPagination]
    )

    const handleSortingChange = useCallback(
        (updater: SortingState | ((old: SortingState) => SortingState)) => {
            const newSorting = typeof updater === 'function' ? updater(sortingRef.current) : updater
            // If externalSorting is provided *and* we're in manual/server-side mode,
            // treat sorting as controlled: do not update internal state, just notify parent.
            // do not update internal state, just notify parent.
            if (!shouldUseExternalSorting) {
                setSorting(newSorting)
            }
            if (isManualPagination && onSortingChange) {
                onSortingChange(newSorting)
            }
        },
        [isManualPagination, onSortingChange, shouldUseExternalSorting]
    )

    // ============================================================================
    // Wrapper handlers for column changes
    // ============================================================================
    const handleColumnOrderChange = useCallback(
        (updater: ColumnOrderState | ((old: ColumnOrderState) => ColumnOrderState)) => {
            setColumnOrder((prev) => {
                const newOrder = typeof updater === 'function' ? updater(prev) : updater
                if (onColumnOrderChange) {
                    onColumnOrderChange(newOrder)
                }

                if (storageKey) {
                    try {
                        const saved = localStorage.getItem(storageKey)
                        const parsed = saved ? JSON.parse(saved) : {}
                        localStorage.setItem(
                            storageKey,
                            JSON.stringify({ ...parsed, order: newOrder })
                        )
                    } catch (e) {
                        // ignore
                    }
                }
                return newOrder
            })
        },
        [onColumnOrderChange, storageKey]
    )

    const handleColumnVisibilityChange = useCallback(
        (updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => {
            setColumnVisibility((prev) => {
                const newVisibility = typeof updater === 'function' ? updater(prev) : updater
                if (onColumnVisibilityChange) {
                    onColumnVisibilityChange(newVisibility)
                }

                if (storageKey) {
                    try {
                        const saved = localStorage.getItem(storageKey)
                        const parsed = saved ? JSON.parse(saved) : {}
                        localStorage.setItem(
                            storageKey,
                            JSON.stringify({ ...parsed, visibility: newVisibility })
                        )
                    } catch (e) {
                        // ignore
                    }
                }
                return newVisibility
            })
        },
        [onColumnVisibilityChange, storageKey]
    )

    // ============================================================================
    // Column Configuration
    // ============================================================================
    // Add row number column if requested
    // Only recreate the array when showRowNumber or columns reference actually changes
    const columnsWithRowNumber = useMemo(() => {
        if (!showRowNumber) return columns
        return [rowNumberColumnRef.current, ...columns]
    }, [showRowNumber, columns])

    // ============================================================================
    // Table Configuration
    // ============================================================================
    const table = useReactTable({
        data,
        columns: columnsWithRowNumber,
        state: {
            sorting: effectiveSorting,
            columnOrder,
            columnVisibility,
            ...(enablePagination ? { pagination: effectivePagination } : {}),
        },
        onSortingChange: handleSortingChange,
        onColumnOrderChange: handleColumnOrderChange,
        onColumnVisibilityChange: handleColumnVisibilityChange,
        ...(enablePagination ? { onPaginationChange: handlePaginationChange } : {}),
        getCoreRowModel: getCoreRowModel(),
        // Only use client-side sorting if not using manual pagination (server-side)
        ...(!isManualPagination ? { getSortedRowModel: getSortedRowModel() } : {}),
        ...(enablePagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
        ...(enablePagination && isManualPagination && pageCount !== undefined ? { manualPagination: true, pageCount } : {}),
    })

    // Total pages used only for displaying pagination controls.
    // When totalPages <= 1, we hide page navigation (but keep page size selector).
    const totalPages = enablePagination ? table.getPageCount() : 1

    // ============================================================================
    // Helper Functions
    // ============================================================================
    // Memoize leaf column IDs for drag-and-drop functionality
    const leafColumnIds = useMemo(() => table.getAllLeafColumns().map(column => column.id), [table])

    /**
     * Handles column reordering when a column is dropped on another column.
     * Updates the column order state to reflect the new arrangement.
     * 
     * @param targetColumnId - The ID of the column where the dragged column is dropped
     */
    const handleDrop = (targetColumnId: string) => {
        if (!draggedColumn || draggedColumn === targetColumnId) return
        const currentOrder = columnOrder.length > 0 ? columnOrder : leafColumnIds
        const fromIndex = currentOrder.indexOf(draggedColumn)
        const toIndex = currentOrder.indexOf(targetColumnId)
        if (fromIndex === -1 || toIndex === -1) return
        const newOrder = [...currentOrder]
        newOrder.splice(fromIndex, 1)
        newOrder.splice(toIndex, 0, draggedColumn)
        handleColumnOrderChange(newOrder)
    }

    // ============================================================================
    // Render
    // ============================================================================
    return (
        <div className={`relative ${containerClassName}`}>
            {onAdd && (
                <div className="absolute left-0 top-0 w-full h-full z-10 pointer-events-none">
                    <div className="fixed right-4 bottom-10 pointer-events-auto">
                        <button
                            type="button"
                            onClick={onAdd}
                            className="flex justify-center items-center bg-theme-50 hover:bg-theme-100 text-theme-800 w-12 h-12 rounded-3xl transition ease-in duration-200"
                        >
                            <PlusIcon className="w-8 h-8" />
                        </button>
                    </div>
                </div>
            )}
            <div className="relative h-full grid grid-rows-list">
                <div className="bg-theme-200 p-2 relative grid grid-cols-1 sm:grid-cols-[1fr_minmax(0,auto)_1fr] items-end gap-2">
                    {/* 中央のブロックは、狭い画面では1行目中央、十分な幅があるときは画面全体の中央カラムに配置 */}
                    <div className="flex flex-wrap justify-center items-center gap-2 sm:col-start-2 sm:col-end-3">
                        <div className="relative column-menu-container">
                            <button
                                type="button"
                                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                                onClick={() => setShowColumnMenu(!showColumnMenu)}
                            >
                                <AdjustmentsHorizontalIcon className="w-5 h-5" />
                            </button>
                            {showColumnMenu && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setShowColumnMenu(false)} />
                                    <div className="absolute left-full ml-1 top-0 w-56 bg-white rounded-md shadow-lg z-50 border border-gray-200 max-h-[calc(100vh-6rem)] overflow-y-auto overflow-x-hidden column-menu-container">
                                        <div className="p-2">
                                            <div className="text-xs font-semibold text-gray-700 mb-2 px-2">表示列</div>
                                            {table
                                                .getAllColumns()
                                                .filter(column => column.id !== 'actions' && column.getCanHide())
                                                .map(column => (
                                                    <label key={column.id} className="flex items-center px-2 py-1 hover:bg-gray-100 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={column.getIsVisible()}
                                                            onChange={() => column.toggleVisibility()}
                                                            className="mr-2"
                                                        />
                                                        <span className="text-sm text-gray-700">
                                                            {typeof column.columnDef.header === 'string'
                                                                ? column.columnDef.header
                                                                : column.id}
                                                        </span>
                                                    </label>
                                                ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        {filterContent}
                    </div>
                    {/* 右側のブロックは、狭い画面では2行目に落とし、十分な幅があるときは3列目右端寄せ */}
                    <div className="flex items-center gap-3 justify-center sm:justify-end sm:col-start-3 sm:col-end-4">
                        {enablePagination && (
                            paginationContent || (
                                <div className="flex items-center gap-0">
                                    <select
                                        className="px-2 py-1 pr-6 text-sm border border-gray-300 rounded appearance-none bg-white bg-[length:16px_16px] bg-[right_0.25rem_center] bg-no-repeat"
                                        style={{
                                            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
                                        }}
                                        value={table.getState().pagination.pageSize}
                                        onChange={e => {
                                            table.setPageSize(Number(e.target.value))
                                        }}
                                    >
                                        {[10, 20, 30, 50, 100].map(pageSize => (
                                            <option key={pageSize} value={pageSize}>
                                                {pageSize}件
                                            </option>
                                        ))}
                                    </select>
                                    {totalPages > 1 && (
                                        <>
                                            <button
                                                type="button"
                                                className="p-1 disabled:opacity-50 disabled:cursor-not-allowed hover:text-theme-600"
                                                onClick={() => table.setPageIndex(0)}
                                                disabled={table.getState().pagination.pageIndex === 0}
                                            >
                                                <ChevronDoubleLeftIcon className="w-5 h-5" />
                                            </button>
                                            <button
                                                type="button"
                                                className="p-1 disabled:opacity-50 disabled:cursor-not-allowed hover:text-theme-600"
                                                onClick={() => table.previousPage()}
                                                disabled={!table.getCanPreviousPage()}
                                            >
                                                <ChevronLeftIcon className="w-5 h-5" />
                                            </button>
                                            <span className="text-sm text-gray-700 whitespace-nowrap">
                                                {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
                                            </span>
                                            <button
                                                type="button"
                                                className="p-1 disabled:opacity-50 disabled:cursor-not-allowed hover:text-theme-600"
                                                onClick={() => table.nextPage()}
                                                disabled={!table.getCanNextPage()}
                                            >
                                                <ChevronRightIcon className="w-5 h-5" />
                                            </button>
                                            <button
                                                type="button"
                                                className="p-1 disabled:opacity-50 disabled:cursor-not-allowed hover:text-theme-600"
                                                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                                disabled={table.getState().pagination.pageIndex === table.getPageCount() - 1}
                                            >
                                                <ChevronDoubleRightIcon className="w-5 h-5" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            )
                        )}
                        {typeof itemCount === 'number' && (
                            <span className="text-sm text-gray-700 whitespace-nowrap">
                                {`${itemCount.toLocaleString()} 件`}
                            </span>
                        )}
                    </div>
                </div>
                <div className="bg-white"></div>
                <div className="px-2 pb-2 h-full overflow-scroll flex flex-col z-0">
                    <div className="overflow-x-auto">
                        <table className={tableClassName}>
                            <thead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => {
                                            const canSort = header.column.getCanSort()
                                            const sortDirection = header.column.getIsSorted()
                                            const isSorted = sortDirection !== false
                                            const isDragging = draggedColumn === header.id

                                            return (
                                                <th
                                                    key={header.id}
                                                    scope="col"
                                                    draggable
                                                    onDragStart={() => setDraggedColumn(header.id)}
                                                    onDragOver={e => {
                                                        e.preventDefault()
                                                        e.dataTransfer.dropEffect = 'move'
                                                    }}
                                                    onDrop={e => {
                                                        e.preventDefault()
                                                        handleDrop(header.id)
                                                        setDraggedColumn(null)
                                                    }}
                                                    onDragEnd={() => setDraggedColumn(null)}
                                                    className={`${canSort ? 'cursor-pointer select-none hover:bg-theme-100' : ''} ${isDragging ? 'opacity-50' : ''}`}
                                                    onClick={canSort ? () => header.column.toggleSorting() : undefined}
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <span className="inline-flex items-center gap-[2px]">
                                                            <span className={isSorted ? 'font-semibold text-gray-900' : ''}>
                                                                {flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                            </span>
                                                            {isSorted && (
                                                                <span className="text-[10px] leading-none">
                                                                    {sortDirection === 'asc' ? '▲' : '▼'}
                                                                </span>
                                                            )}
                                                        </span>
                                                    )}
                                                </th>
                                            )
                                        })}
                                    </tr>
                                ))}
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {table.getRowModel().rows.map(row => {
                                    const rowProps = getRowProps?.(row) ?? {}
                                    const { key, ...restRowProps } = rowProps
                                    return (
                                        <tr key={(key as React.Key) ?? row.id} {...restRowProps}>
                                            {row.getVisibleCells().map(cell => {
                                                const cellProps = getCellProps?.(cell) ?? {}
                                                const { key: cellKey, ...restCellProps } = cellProps
                                                return (
                                                    <td key={(cellKey as React.Key) ?? cell.id} {...restCellProps}>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    )
}

